<script setup>
import XscpConnectionFailure from '@/StoreWeb/components/miscellaneous/conduent-support-reader/xscp/errors/XscpConnectionFailure'
import XscpDriverIssue from '@/StoreWeb/components/miscellaneous/conduent-support-reader/xscp/errors/XscpDriverIssue'
import XscpLaunchDetectFailure from '@/StoreWeb/components/miscellaneous/conduent-support-reader/xscp/errors/XscpLaunchDetectFailure'
import XscpLaunchFailure from '@/StoreWeb/components/miscellaneous/conduent-support-reader/xscp/errors/XscpLaunchFailure'
import XscpWaitingFailure from '@/StoreWeb/components/miscellaneous/conduent-support-reader/xscp/errors/XscpWaitingFailure'
import XscpDrivers from '@/StoreWeb/components/miscellaneous/conduent-support-reader/xscp/XscpDrivers'
import config from 'config'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

const emit = defineEmits(['descriptionUpdated', 'componentUpdated', 'propsUpdated', 'scriptLoaded', 'stateUpdated', 'xscpReady'])

const store = useStore()
const { t } = useI18n()

const isCardReaderInstallStepDone = ref(false)
const isXscpInitialized = ref(false)
const isXscpLaunching = ref(false)
const isXscpLaunched = ref(false)
const launchTimeout = ref(null)
const xscpLaunchingAttempted = ref(false)

const informationDuration = 2000
const checkDuration = 5000

const debugInformationsDisplay = computed(() => store.state.debuggingModule.debugInformationsDisplay)

function cleanStep () {
    emit('stateUpdated', '')
    emit('descriptionUpdated', '')
    emit('componentUpdated', null)
    emit('propsUpdated', null)
}

function reinitValues () {
    xscpLaunchingAttempted.value = false
    isXscpLaunching.value = false
    isXscpLaunched.value = false

    isCardReaderInstallStepDone.value = localStorage.getItem('isCardReaderInstallStepDone') || false
    const installedXscpVersion = localStorage.getItem('installedXscpVersion')

    if (installedXscpVersion && installedXscpVersion !== config.support_reader.xscp_version) {
        isCardReaderInstallStepDone.value = false
    }
}

/* ------------------------------------------------------------- */
/*                          CONNECTION                           */
/* ------------------------------------------------------------- */
function init () {
    cleanStep()
    reinitValues()
    emit('stateUpdated', 'loading')
    emit('descriptionUpdated', t('card_reader:init_connection:in_progress:message'))
    window.InitXSCP()
}

function onInitXscpHandler (event) {
    if (debugInformationsDisplay.value > 1) {
        // eslint-disable-next-line
        console.log(event.type, event.detail)
    }
}

function onEndInitXscpHandler (event) {
    cleanStep()

    if (debugInformationsDisplay.value > 1) {
        // eslint-disable-next-line
        console.log(event.type, event.detail)
    }

    if (event.detail.response === 0) {
        isXscpInitialized.value = true
        emit('descriptionUpdated', t('card_reader:init_connection:success:message'))

        // 2s is necessary here to show to the user that the connection is done
        setTimeout(() => {
            if (isCardReaderInstallStepDone.value) {
                launchXSCP()
            } else {
                installDrivers()
            }
        }, informationDuration)
    } else {
        emit('stateUpdated', 'error')
        emit('componentUpdated', XscpConnectionFailure)
    }
}

/* ------------------------------------------------------------- */
/*                        INSTALLATION                           */
/* ------------------------------------------------------------- */
function installDrivers () {
    cleanStep()
    localStorage.removeItem('isCardReaderInstallStepDone')
    emit('componentUpdated', XscpDrivers)
}

function downloadXSCP () {
    window.DownloadXSCP(`${window.conduentMobilitySolutionsUrl}/httptunnel/`)
}

function onDownloadXscpHandler (event) {
    if (debugInformationsDisplay.value > 1) {
        // eslint-disable-next-line
        console.log(event.type, event.detail)
    }
}

/* ------------------------------------------------------------- */
/*                       LAUNCH PROGRAM                          */
/* ------------------------------------------------------------- */
function launchXSCP () {
    cleanStep()
    emit('stateUpdated', 'loading')
    emit('descriptionUpdated', t('card_reader:launch_program:launch_in_progress:message'))
    window.LaunchXSCP()
}

function onLaunchXscpHandler (event) {
    if (debugInformationsDisplay.value > 1) {
        // eslint-disable-next-line
        console.log(event.type, event.detail)
    }

    if (event.detail.response === -2) {
        emit('stateUpdated', 'error')
        emit('propsUpdated', {
            description: t('card_reader:launch_program:xscp_not_found:instructions:message'),
            title: t('card_reader:launch_program:xscp_not_found:message')
        })
        emit('componentUpdated', XscpDriverIssue)
    } else if (event.detail.response === -1) {
        emit('stateUpdated', 'error')
        emit('componentUpdated', XscpLaunchFailure)
    } else {
        emit('stateUpdated', 'loading')
        emit('descriptionUpdated', t('card_reader:launch_program:waiting_for_authorization:message'))
        isXscpLaunching.value = true

        // 5s is necessary here to let the user click on the browser popup
        launchTimeout.value = setTimeout(launchCheck, checkDuration)
    }
}

function onEndLaunchXscpHandler (event) {
    if (debugInformationsDisplay.value > 1) {
        // eslint-disable-next-line
        console.log(event.type, event.detail)
    }

    if (event.detail.response === 0) {
        clearTimeout(launchTimeout.value)
        isXscpLaunching.value = false
        isXscpLaunched.value = true
        emit('descriptionUpdated', t('card_reader:launch_program:success:message'))
        getXSCPVersion()
    } else {
        emit('stateUpdated', 'error')

        if (event.detail.response === -5) {
            emit('propsUpdated', {
                description: t('card_reader:launch_program:xscp_launch_obsolete_version:instructions:message'),
                title: t('card_reader:launch_program:xscp_obsolete_version:message')
            })
            emit('componentUpdated', XscpDriverIssue)
        } else {
            emit('descriptionUpdated', t('card_reader:launch_program:launch_failed:message'))
        }
    }
}

async function launchCheck () {
    if (!xscpLaunchingAttempted.value && !isXscpLaunched.value && isXscpLaunching.value) {
        detectXSCP()
    } else {
        if (debugInformationsDisplay.value > 1) {
            // eslint-disable-next-line
            console.log('launchCheck', xscpLaunchingAlreadyFailed.value, !isXscpLaunched.value, isXscpLaunching.value)
        }
        xscpLaunchingAttempted.value = false
        emit('stateUpdated', 'error')
        emit('componentUpdated', XscpWaitingFailure)
    }
}

function detectXSCP () {
    cleanStep()
    emit('stateUpdated', 'loading')
    emit('descriptionUpdated', t('card_reader:launch_program:check_launch_in_progress:message'))
    window.DetectXSCP()
}

function onDetectXscpHandler (event) {
    if (debugInformationsDisplay.value > 1) {
        // eslint-disable-next-line
        console.log(event.type, event.detail)
    }

    if (event.detail.response === -1) {
        emit('stateUpdated', 'error')
        emit('componentUpdated', XscpLaunchFailure)
        clearTimeout(launchTimeout.value)
    }
}

function onEndDetectXscpHandler (event) {
    if (debugInformationsDisplay.value > 1) {
        // eslint-disable-next-line
        console.log(event.type, event.detail)
    }

    if (event.detail.response === -1) {
        emit('stateUpdated', 'error')
        emit('componentUpdated', XscpLaunchDetectFailure)
        xscpLaunchingAttempted.value = true
    } else {
        emit('descriptionUpdated', t('card_reader:launch_program:success:message'))
        getXSCPVersion()
    }
}

function getXSCPVersion () {
    emit('stateUpdated', 'loading')
    emit('descriptionUpdated', t('card_reader:launch_program:get_version_in_progress:message'))
    window.GetVersion()
}

function onGetVersionHandler (event) {
    if (debugInformationsDisplay.value > 1) {
        // eslint-disable-next-line
        console.log(event.type, event.detail)
    }
}

function onEndGetVersionHandler (event) {
    if (debugInformationsDisplay.value > 1) {
        // eslint-disable-next-line
        console.log(event.type, event.detail)
    }

    if (typeof event.detail.response === 'string') {
        localStorage.setItem('installedXscpVersion', event.detail.response)
        localStorage.setItem('isCardReaderInstallStepDone', 1)
        emit('descriptionUpdated', t('card_reader:launch_program:get_version_success:message').replace('%xscpVersion%', event.detail.response))

        // 2s is necessary here to show to the user that the plugin is ready
        setTimeout(() => {
            reinitValues()

            emit('xscpReady')
        }, informationDuration)
    }
}

/* ------------------------------------------------------------- */
/*                         STOP PROGRAM                          */
/* ------------------------------------------------------------- */
function callStopXSCP () {
    window.StopXSCP()
}

function onStopXscpHandler (event) {
    if (debugInformationsDisplay.value > 1) {
        // eslint-disable-next-line
        console.log(event.type, event.detail)
    }
}

/* ------------------------------------------------------------- */
/*                     MOUNTED / UNMOUNTED                       */
/* ------------------------------------------------------------- */
onMounted(() => {
    window.conduentMobilitySolutionsUrl = config.support_reader.conduent_mobility_solutions_url
    window.addEventListener('unload', callStopXSCP)
    document.body.addEventListener('card-reader-init-xscp', onInitXscpHandler)
    document.body.addEventListener('card-reader-end-init-xscp', onEndInitXscpHandler)
    document.body.addEventListener('card-reader-launch-xscp', onLaunchXscpHandler)
    document.body.addEventListener('card-reader-end-launch-xscp', onEndLaunchXscpHandler)
    document.body.addEventListener('card-reader-detect-xscp', onDetectXscpHandler)
    document.body.addEventListener('card-reader-end-detect-xscp', onEndDetectXscpHandler)
    document.body.addEventListener('card-reader-stop-xscp', onStopXscpHandler)
    document.body.addEventListener('card-reader-get-version', onGetVersionHandler)
    document.body.addEventListener('card-reader-end-get-version', onEndGetVersionHandler)
    document.body.addEventListener('card-reader-download-xscp', onDownloadXscpHandler)

    function loadScript (url, callback) {
        const script = document.createElement('script')
        script.type = 'text/javascript'
        script.onload = function () {
            callback()
        }

        script.src = url
        document.getElementsByTagName('head')[0].appendChild(script)
    }

    const cardReaderScripPath = `${process.env.VUE_APP_CDN_URL}js/${process.env.VUE_APP_NETWORK_ID}/conduent-dynamic.js`
    loadScript(cardReaderScripPath, function () {
        emit('scriptLoaded')
        init()
    })
})

onUnmounted(() => {
    if (isXscpInitialized.value) {
        callStopXSCP()
    }

    window.removeEventListener('unload', callStopXSCP)
    document.body.removeEventListener('card-reader-init-xscp', onInitXscpHandler)
    document.body.removeEventListener('card-reader-end-init-xscp', onEndInitXscpHandler)
    document.body.removeEventListener('card-reader-launch-xscp', onLaunchXscpHandler)
    document.body.removeEventListener('card-reader-end-launch-xscp', onEndLaunchXscpHandler)
    document.body.removeEventListener('card-reader-detect-xscp', onDetectXscpHandler)
    document.body.removeEventListener('card-reader-end-detect-xscp', onEndDetectXscpHandler)
    document.body.removeEventListener('card-reader-stop-xscp', onStopXscpHandler)
    document.body.removeEventListener('card-reader-get-version', onGetVersionHandler)
    document.body.removeEventListener('card-reader-end-get-version', onEndGetVersionHandler)
    document.body.removeEventListener('card-reader-download-xscp', onDownloadXscpHandler)
})

/* ------------------------------------------------------------- */
/*                        EXPOSED METHODS                        */
/* ------------------------------------------------------------- */
defineExpose({ downloadXSCP, init, installDrivers, launchXSCP })
</script>
